import React from "react";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import toast from "react-hot-toast";
import { Button } from "../ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../ui/input-otp";

type OtpFieldProps = {
  value: string;
  onChange: (value: string) => void;
  length?: number;
  disabled?: boolean;
  autoFocus?: boolean;
  /** Fires once the final digit is entered — lets the form auto-submit. */
  onComplete?: (value: string) => void;
};

/**
 * The single OTP input for the whole app. Replaces react-otp-input,
 * react18-input-otp and reactjs-otp-input, which were each used by different
 * screens with slightly different behaviour.
 *
 * `input-otp` renders one real input behind the slots, so native paste,
 * autofill and iOS SMS one-time-code all work without a manual paste button —
 * the explicit button is kept because the old UI had one.
 */
const OtpField = React.forwardRef<HTMLInputElement, OtpFieldProps>(({
  value,
  onChange,
  length = 6,
  disabled,
  autoFocus = true,
  onComplete,
  ...props
}, ref) => {
  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const digits = text.replace(/\D/g, "").slice(0, length);
      if (digits.length === length) {
        onChange(digits);
        onComplete?.(digits);
      } else {
        toast.error(`Clipboard does not contain a valid ${length}-digit code.`);
      }
    } catch {
      toast.error("Could not read your clipboard. Paste the code manually.");
    }
  };

  return (
    <div className="flex flex-col items-center gap-4 lgss:items-start">
      <InputOTP
        ref={ref}
        maxLength={length}
        value={value}
        onChange={onChange}
        onComplete={onComplete}
        disabled={disabled}
        autoFocus={autoFocus}
        pattern={REGEXP_ONLY_DIGITS}
        inputMode="numeric"
        containerClassName="gap-2"
        {...props}
      >
        <InputOTPGroup className="gap-2">
          {Array.from({ length }).map((_, index) => (
            <InputOTPSlot
              key={index}
              index={index}
              className="size-12 rounded-xl border-2 border-l border-input text-[20px] font-semibold first:rounded-l-xl last:rounded-r-xl"
            />
          ))}
        </InputOTPGroup>
      </InputOTP>

      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={handlePaste}
        className="bg-accent font-sora text-[13px] font-semibold text-primary hover:bg-accent/70"
      >
        Paste code
      </Button>
    </div>
  );
});
OtpField.displayName = "OtpField";

export default OtpField;
